import React, { useCallback } from 'react'
import { useFeatureFlags } from '@node-space/hooks'
import { Avatar, ComboboxItem } from '@node-space/ui-components'
import { CustomerWallet } from 'types/customers'
import { CurrencyBase, WalletType } from 'types/types'
import { floorWithPrecision, roundNumberWithCommas } from 'utils/utils'
import { getIsWalletStatusSupported } from 'utils/wallet'
import { IGNORED_CURRENCIES } from './queries'

export const useSupportedCurrencies = () => {
  const { enableUsLaunchRestrictions, enableUsRestrictedCurrencies, enableMaltaAccounts } =
    useFeatureFlags()
  const disallowPaymentCurrencies = enableMaltaAccounts?.disallowPaymentCurrencies ?? []

  const getIsUsSupportedCurrency = (currency: CurrencyBase) => {
    if (enableUsRestrictedCurrencies?.length) {
      return currency?.fiat || enableUsRestrictedCurrencies?.includes(currency?.code)
    }
    if (enableUsLaunchRestrictions) return currency?.fiat
    return true
  }

  const getIsSupportedCurrency = (currency: CurrencyBase) => {
    if (!currency) return false

    return (
      !IGNORED_CURRENCIES.includes(currency?.code) &&
      getIsUsSupportedCurrency(currency) &&
      !disallowPaymentCurrencies.includes(currency?.code)
    )
  }

  const getSupportedCurrencies = (currencies: CurrencyBase[]) => {
    if (!currencies) return []
    return currencies?.filter(currency => getIsSupportedCurrency(currency))
  }

  const getCurrencyOptions = (currencies: CurrencyBase[]): ComboboxItem[] => {
    if (!currencies) return []
    return currencies?.map(currency => ({
      value: currency?.code,
      leftText: `${currency?.name ?? ''} (${currency?.code ?? ''})`,
      LeftElement: <Avatar name={currency?.code} size="small" />,
    }))
  }

  const getSupportedCurrencyWallets = (wallets: WalletType[], isActiveOnly?: boolean) => {
    if (!wallets) return []
    return wallets?.filter(wallet => {
      const isStatusSupported = getIsWalletStatusSupported(wallet?.status, isActiveOnly)
      const isCurrencySupported = getIsSupportedCurrency(wallet?.currency)
      return isStatusSupported && isCurrencySupported
    })
  }

  const getWalletOptions = (
    wallets: WalletType[],
    isApproximateBalance?: boolean
  ): ComboboxItem[] => {
    if (!wallets) return []
    return wallets?.map(wallet => {
      const code = wallet?.currency?.code
      const balance = isApproximateBalance
        ? roundNumberWithCommas(wallet?.approxBalance, 2)
        : floorWithPrecision(wallet?.balance || 0, wallet?.currency?.quantityPrecision)
      return {
        LeftElement: <Avatar name={code?.toLowerCase()} size="small" />,
        leftText: wallet?.description,
        value: wallet?.lsid,
        rightText: ` ${balance ?? ''} ${code ?? ''}`,
      }
    })
  }

  const getCustomerWalletOptions = useCallback((wallets: CustomerWallet[]): ComboboxItem[] => {
    if (!wallets) return []
    return wallets.map(wallet => {
      const code = wallet.balance.currencyCode
      const balance = wallet.balance.value
      return {
        LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
        leftText: wallet.name,
        value: wallet.id,
        rightText: ` ${balance} ${code}`,
      }
    })
  }, [])

  return {
    getIsSupportedCurrency,
    getSupportedCurrencies,
    getCurrencyOptions,
    getSupportedCurrencyWallets,
    getWalletOptions,
    getCustomerWalletOptions,
  }
}
